import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  whatsappInboxService, WhatsAppConversation, WhatsAppMessage, WhatsAppInternalNote,
} from '@/services/integrations/whatsappInboxService';
import { useConversationThread } from '@/hooks/useConversationThread';
import { useWhatsAppSocketEvents } from '@/hooks/useWhatsAppSocket';
import { formatDateSeparator } from '@/utils/whatsappInbox';
import ConversationHeader from './ConversationHeader';
import WindowStatusBanner from './WindowStatusBanner';
import DateSeparator from './DateSeparator';
import MessageBubble from './MessageBubble';
import InternalNoteBubble from './InternalNoteBubble';
import Composer from './Composer';

const DEGRADED_CONVERSATION_POLL_MS = 15_000;

type TimelineItem =
  | { kind: 'message'; at: string; message: WhatsAppMessage }
  | { kind: 'note'; at: string; note: WhatsAppInternalNote };

interface ChatPanelProps {
  conversation: WhatsAppConversation;
  currentUserName: string;
  onConversationPatch: (patch: Partial<WhatsAppConversation>) => void;
}

const ChatPanel: React.FC<ChatPanelProps> = ({ conversation, currentUserName, onConversationPatch }) => {
  const { messages, hasMore, loading, loadingMore, loadMore, refresh } = useConversationThread(conversation.uuid);
  const [notes, setNotes] = useState<WhatsAppInternalNote[]>([]);
  const [templatePickerSignal, setTemplatePickerSignal] = useState<number | undefined>(undefined);
  const scrollRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const prevMessageCount = useRef(0);

  const loadNotes = useCallback(() => {
    whatsappInboxService.listNotes(conversation.uuid).then(setNotes);
  }, [conversation.uuid]);

  useEffect(() => { loadNotes(); }, [loadNotes]);

  // Only the message thread refreshes live on its own — the conversation
  // object itself (last_inbound_at, unread_count, status, opted_out) is a
  // static snapshot from whenever it was selected in the list. Without this,
  // a genuine new reply updates last_inbound_at in the DB but the OPEN
  // ChatPanel keeps computing the 24h window from the stale value it was
  // handed at selection time — showing "window closed Xh ago" using a
  // last_inbound_at from hours or days before the real most-recent reply.
  const refreshConversation = useCallback(async () => {
    if (!conversation.lead_uuid) return;
    const result = await whatsappInboxService.listConversations({
      lead_uuid: conversation.lead_uuid, page_size: 1,
    });
    if (result.data[0]) onConversationPatch(result.data[0]);
  }, [conversation.lead_uuid, onConversationPatch]);

  const { degraded: conversationDegraded } = useWhatsAppSocketEvents(
    useCallback((event) => {
      if (event.conversation_uuid !== conversation.uuid) return;
      // A reply arriving while this thread is on screen has already been read by
      // whoever is looking at it, but the webhook still incremented unread_count.
      // Without this the badge reappears on an open conversation.
      whatsappInboxService.markRead(conversation.uuid);
      refreshConversation();
    }, [conversation.uuid, refreshConversation]),
  );

  useEffect(() => {
    if (!conversationDegraded) return;
    const id = setInterval(refreshConversation, DEGRADED_CONVERSATION_POLL_MS);
    return () => clearInterval(id);
  }, [conversationDegraded, refreshConversation]);

  // Auto-scroll to bottom only when new messages actually arrive, not on
  // every render — otherwise loading older history would yank the view down.
  useEffect(() => {
    if (messages.length > prevMessageCount.current) {
      bottomRef.current?.scrollIntoView({ behavior: prevMessageCount.current === 0 ? 'auto' : 'smooth' });
    }
    prevMessageCount.current = messages.length;
  }, [messages.length]);

  const timeline = useMemo<TimelineItem[]>(() => {
    const items: TimelineItem[] = [
      ...messages.map((m): TimelineItem => ({ kind: 'message', at: m.created_at, message: m })),
      ...notes.map((n): TimelineItem => ({ kind: 'note', at: n.created_at, note: n })),
    ];
    items.sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());
    return items;
  }, [messages, notes]);

  const handleRetry = useCallback((m: WhatsAppMessage) => {
    if (m.msg_type !== 'text' || !m.body) return;
    whatsappInboxService.sendText(conversation.uuid, m.body).then((r) => {
      if (r.ok) refresh();
    });
  }, [conversation.uuid, refresh]);

  return (
    <div className="flex-1 flex flex-col min-w-0 min-h-0 bg-gray-50 relative">
      <ConversationHeader conversation={conversation} onSent={refresh} />

      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-6 py-4 flex flex-col gap-2.5 min-h-0"
        style={{
          backgroundColor: '#f7f2ef',
          backgroundImage: 'url(https://zeroharm-crm.sfo3.cdn.digitaloceanspaces.com/whatsapp_outbound_media/walpaper%20whatsaap.jfif)',
          backgroundRepeat: 'repeat',
        }}
      >
        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-brandColor" />
          </div>
        ) : (
          <>
            {hasMore && (
              <div className="flex justify-center pb-2">
                <button
                  onClick={loadMore}
                  disabled={loadingMore}
                  className="text-xs font-semibold text-gray-500 hover:text-gray-700 disabled:opacity-50"
                >
                  {loadingMore ? 'Loading…' : 'Load older messages'}
                </button>
              </div>
            )}
            {timeline.map((item, i) => {
              const prev = timeline[i - 1];
              const showSeparator = !prev || formatDateSeparator(prev.at) !== formatDateSeparator(item.at);
              const key = item.kind === 'message' ? `m-${item.message.uuid}` : `n-${item.note.uuid}`;
              return (
                <React.Fragment key={key}>
                  {showSeparator && <DateSeparator label={formatDateSeparator(item.at)} />}
                  {item.kind === 'message' ? (
                    <MessageBubble
                      message={item.message}
                      onRetry={item.message.msg_type === 'text' ? handleRetry : undefined}
                    />
                  ) : (
                    <InternalNoteBubble note={item.note} />
                  )}
                </React.Fragment>
              );
            })}
            <div ref={bottomRef} />
          </>
        )}
      </div>

      <WindowStatusBanner
        conversation={conversation}
        onChooseTemplate={() => setTemplatePickerSignal(Date.now())}
      />

      <Composer
        conversation={conversation}
        currentUserName={currentUserName}
        onAfterSend={refresh}
        onAfterNote={loadNotes}
        openTemplatePickerSignal={templatePickerSignal}
      />
    </div>
  );
};

export default ChatPanel;
